// frontend/src/i18n/types.ts
/**
 * Type definitions for the internationalization (i18n) system.
 * Designed for scalability and contextual translation support.
 */

/**
 * Supported languages in the application.
 * Top 15 priority languages + Catalan and Euskera (17 total).
 */
export type SupportedLanguage =
  | "en" // English (default)
  | "zh" // Chinese (Simplified)
  | "es" // Spanish
  | "hi" // Hindi
  | "ar" // Arabic
  | "pt" // Portuguese
  | "fr" // French
  | "de" // German
  | "ja" // Japanese
  | "ko" // Korean
  | "it" // Italian
  | "ru" // Russian
  | "nl" // Dutch
  | "tr" // Turkish
  | "pl" // Polish
  | "ca" // Catalan
  | "eu"; // Euskera (Basque)

/**
 * Metadata categories for translation strings.
 * Helps organize and locate strings.
 */
export type TranslationCategory =
  | "navigation" // Nav bars, menus, links
  | "actions" // Buttons, CTAs
  | "forms" // Input labels, placeholders, validation
  | "clinical" // Medical/clinical terminology
  | "patients" // Patient-related UI
  | "patients_v2" // V2 cards-UI AddPatientForm refactor (cursor_logs/030 § 2026-05-22)
  | "billing" // Payment, subscriptions, usage
  | "auth" // Login, registration, permissions
  | "notifications" // Alerts, toasts, messages
  | "tables" // Table headers, pagination
  | "modals" // Dialog titles, confirmations
  | "errors" // Error messages
  | "settings" // User/app settings
  | "dashboard" // Dashboard-specific
  | "chat" // Chat interface, messages
  | "bulk_upload" // Bulk upload (folder, ZIP, CSV)
  | "tutorial" // Onboarding tutorial
  | "legal" // Legal compliance, T&C, consent
  | "environments" // Environment management
  | "tier_gate" // Upgrade prompts, feature gating
  | "contracts_wizard" // Legal contracts wizard
  | "admin_legal" // Superadmin legal review
  | "manage_env" // Environment management actions
  | "env_modal" // Environment creation/edit modals
  | "share_env" // Environment sharing UI
  | "error_tickets" // Error ticket system
  | "monitoring" // System monitoring dashboard
  | "tier_editor" // Subscription tier editor
  | "landing" // Landing page, hero, pricing
  | "network" // Network status messages
  | "documents" // Document viewers, previews
  | "document" // Single document viewer
  | "trials" // Clinical trial matching
  | "patient" // Single patient actions (delete, create)
  | "editable" // Editable table UI
  | "data_panel" // Patient data panel sections
  | "patient_details" // Patient details layout
  | "admin" // Superadmin panel tables and config
  | "admin" // Admin panels, system config
  | "agent_chat" // Agentic chat interface
  | "export" // Data export dialogs
  | "general" // General/shared labels
  | "common"; // Shared/generic strings

/**
 * A single translatable string with full context.
 * This structure enables translators to work independently
 * without needing access to the actual UI.
 */
export interface TranslationEntry {
  /** The text to display (in the source language) */
  text: string;

  /**
   * Detailed description of where/how this text is used.
   * Should include:
   * - Component/page location
   * - Visual context (button, header, tooltip, etc.)
   * - Any behavioral context (what happens when clicked)
   */
  context: string;

  /** Category for organization */
  category: TranslationCategory;

  /**
   * Maximum character length for UI constraints.
   * Helps translators avoid text overflow.
   */
  maxLength?: number;

  /**
   * List of placeholder variables in the text.
   * E.g., ["name", "count"] for "Hello {name}, you have {count} items"
   */
  placeholders?: string[];

  /**
   * Notes for translators about tone, formality, etc.
   * E.g., "Use formal register" or "Medical term - may need localization"
   */
  translatorNotes?: string;

  /**
   * Whether this string contains HTML/JSX that should be preserved.
   * If true, translators should not modify tag structure.
   */
  containsMarkup?: boolean;

  /**
   * Reference to related strings for consistency.
   * E.g., ["action.cancel"] to maintain consistent wording
   */
  relatedKeys?: string[];

  /**
   * Whether this string still needs to be translated to the other 16 languages.
   * Set to true when adding new English strings; removed after batch translation.
   */
  needsTranslation?: boolean;
}

/**
 * A flat dictionary of translation entries keyed by their ID.
 * Keys follow dot notation: category.subcategory.identifier
 */
export type TranslationDictionary = Record<string, TranslationEntry>;

/**
 * A translated dictionary with just the text values.
 * Used at runtime after translation is complete.
 */
export type TranslatedStrings = Record<string, string>;

/**
 * Language metadata for the language selector.
 */
export interface LanguageInfo {
  code: SupportedLanguage;
  name: string; // English name
  nativeName: string; // Name in native script
  direction: "ltr" | "rtl";
  region?: string; // Primary region/country
}

/**
 * Available languages with metadata.
 * Sorted alphabetically by nativeName (Latin-script first, then other scripts).
 */
export const SUPPORTED_LANGUAGES: LanguageInfo[] = [
  { code: "ca", name: "Catalan", nativeName: "Català", direction: "ltr", region: "Catalonia/Valencia/Balearic" },
  { code: "de", name: "German", nativeName: "Deutsch", direction: "ltr", region: "Germany" },
  { code: "en", name: "English", nativeName: "English", direction: "ltr", region: "Global" },
  { code: "es", name: "Spanish", nativeName: "Español", direction: "ltr", region: "Spain/LATAM" },
  { code: "eu", name: "Basque", nativeName: "Euskara", direction: "ltr", region: "Basque Country" },
  { code: "fr", name: "French", nativeName: "Français", direction: "ltr", region: "France/Africa" },
  { code: "it", name: "Italian", nativeName: "Italiano", direction: "ltr", region: "Italy" },
  { code: "nl", name: "Dutch", nativeName: "Nederlands", direction: "ltr", region: "Netherlands" },
  { code: "pl", name: "Polish", nativeName: "Polski", direction: "ltr", region: "Poland" },
  { code: "pt", name: "Portuguese", nativeName: "Português", direction: "ltr", region: "Brazil/Portugal" },
  { code: "tr", name: "Turkish", nativeName: "Türkçe", direction: "ltr", region: "Turkey" },
  { code: "ru", name: "Russian", nativeName: "Русский", direction: "ltr", region: "Russia" },
  { code: "ar", name: "Arabic", nativeName: "العربية", direction: "rtl", region: "MENA" },
  { code: "hi", name: "Hindi", nativeName: "हिन्दी", direction: "ltr", region: "India" },
  { code: "zh", name: "Chinese", nativeName: "中文", direction: "ltr", region: "China" },
  { code: "ja", name: "Japanese", nativeName: "日本語", direction: "ltr", region: "Japan" },
  { code: "ko", name: "Korean", nativeName: "한국어", direction: "ltr", region: "South Korea" },
];

/**
 * Default language for the application.
 */
export const DEFAULT_LANGUAGE: SupportedLanguage = "en";
